/**
 * AI 결과 → 반영 계획 (순수). 실제 저장은 서비스가 actor = 'ai-import'로 한다.
 *
 * 안전 규칙 (AI_PROTOCOL §6·§14, SPEAKER_ROLE §9, Amendment A4):
 * - AI는 FACT를 만들지 않는다. 내담자 진술은 인용한 전사 줄이 모두 높은 신뢰도의 내담자 발언이어야 확인 없이 쓰인다.
 *   그렇지 않으면 needsVerification = true (자동 근거 아님).
 * - AI의 해석은 모두 가설. 정서·인지 등은 observation/hypothesis로만.
 * - 공식 진단 확정(externally_confirmed)은 받지 않는다 → possible로 낮추고 경고.
 * - 위험도·목표 상태·사례개념화·진단적 고려는 '제안'으로만 만든다 (상담자 승인 필요).
 * - 승인된 상담기록지는 건드리지 않는다. 상담자가 직접 고친 칸도 덮지 않는다.
 * - AI의 화자 검토는 자동 판정 줄에만, 신뢰도 상한 0.84 (단독으로 CLIENT_REPORT 근거가 되지 못함).
 */
import type {
  AnalysisItem,
  CaseFormulationRevision,
  CounselingGoal,
  CounselingJournal,
  TranscriptSegment,
} from '../schema/v1';
import type { ProfileField } from '../workflow/profileMerge';
import { CLIENT_REPORT_MIN_ROLE_CONFIDENCE } from '../rules/writeRules';
import { charSimilarity } from '../transcript/normalize';
import type { SessionAnalysisV1 } from './parse';
import { FORMULATION_ITEM_FIELDS } from '../workflow/formulation';

export const AI_ROLE_CONFIDENCE_CAP = 0.84;

export type PlannedEvidence = {
  evidenceType: 'CLIENT_REPORT' | 'COUNSELOR_OBSERVATION' | 'HYPOTHESIS';
  text: string;
  sourceSegmentIds?: string[];
  sourceTimeMs?: number;
  roleAtExtraction?: 'counselor' | 'client' | 'unknown' | 'mixed';
  roleConfidenceAtExtraction?: number;
  agreementStrength?: 'explicit' | 'weak-agreement';
  needsVerification: boolean;
};

export type ImportPlan = {
  analysis: {
    sessionSummary?: string;
    keyEvents: string[];
    notableChanges: string[];
    emotions: AnalysisItem[];
    cognitions: AnalysisItem[];
    behaviors: AnalysisItem[];
    relationshipPatterns: AnalysisItem[];
    interventions: {
      interventionName: string;
      theoreticalOrientation?: string;
      clientResponse?: string;
      followUpNeeded?: boolean;
    }[];
  };
  evidence: PlannedEvidence[];
  risk: Record<
    string,
    { status: 'not_assessed' | 'denied' | 'present' | 'unclear'; followUpRequired?: boolean }
  > | null;
  goals: { goalId: string; title: string; from: string; to: string; note?: string }[];
  formulation: {
    currentProblems?: string[];
    addedTexts: string[];
    /** 칸별 새 항목 (이미 그 칸에 있는 것은 뺌) */
    addedItems: Record<string, string[]>;
    strengthenedIds: string[];
    weakenedIds: string[];
    rejectedIds: string[];
    changeReason?: string;
    /** 기존 가설과 연결하지 못한 강화·약화·폐기 의견: 개정안에 남겨 상담자가 연결·추가·삭제 */
    unmatched: { kind: 'strengthened' | 'weakened' | 'rejected'; text: string }[];
    /** 연결할 수 있는 현재 가설 (미리보기에서 고르기용) */
    hypothesisOptions: { id: string; text: string }[];
  } | null;
  diagnosis: SessionAnalysisV1['diagnosis'];
  journal: { fields: Record<string, string>; skipped: string[] } | null;
  roleReviews: { segmentId: string; role: 'counselor' | 'client'; from: string; text: string; reason?: string }[];
  /** A33: 치료 접근 근거 (판단 자료) */
  approachEvidence: SessionAnalysisV1['approachEvidence'];
  verbatims: {
    segmentIds: string[];
    startMs: number;
    endMs: number;
    reason?: string;
    supervisionQuestions: string[];
  }[];
  questions: string[];
  /** 내담자 정보 덧붙이기: ok = 내담자 발언으로 확인됨 (반영), 아니면 반영하지 않고 보여주기만 */
  profile: {
    field: ProfileField;
    value: string;
    segmentIds: string[];
    changed?: boolean;
    ok: boolean;
    reason?: string;
  }[];
  /** 확인할 점 (전체 문장 + 근거 줄): 화면에서 전사 줄로 바로 이동할 수 있게 */
  issues: {
    kind: 'counselor-quoted' | 'missing-line';
    message: string;
    text: string;
    segmentIds: string[];
    lines?: { segmentId: string; role: string; sure: boolean; text: string }[];
  }[];
  /** V2 */
  goalSuggestions: SessionAnalysisV1['goalSuggestions'];
  dialogueDraft: Record<string, string>;
  selfReview: SessionAnalysisV1['selfReview'];
  directionPlan: SessionAnalysisV1['directionPlan'];
  changePoints: SessionAnalysisV1['changePoints'];
  missingSections: string[];
  warnings: string[];
  /** 화면 미리보기용: 상담자 승인이 필요한 변경 */
  needsApproval: string[];
};

export type PlanContext = {
  segments: TranscriptSegment[];
  goals: CounselingGoal[];
  formulation?: CaseFormulationRevision;
  journal?: CounselingJournal | null;
  newId: () => string;
};

const HYP_FIELDS = [
  'beliefHypotheses',
  'coreCognitiveThemes',
  'relationalPatterns',
  'behavioralPatterns',
  'coreEmotions',
  'allianceFactors',
  'therapyInterferingPatterns',
] as const;

function matchHypothesis(text: string, f?: CaseFormulationRevision): string | undefined {
  if (!f) return undefined;
  let best: { id: string; s: number } | undefined;
  for (const k of HYP_FIELDS)
    for (const it of f[k] ?? []) {
      const s = it.text === text ? 1 : charSimilarity(it.text, text);
      if (s >= 0.72 && (!best || s > best.s)) best = { id: it.id, s };
    }
  return best?.id;
}

/** AI가 문장 앞에 붙인 '(상담자 확인)', '(화자 확인)' 같은 꼬리표는 저장하지 않는다 */
const untag = (t: string) => t.replace(/^\s*[([](상담자|화자|내담자)\s*확인[^)\]]*[)\]]\s*/u, '').trim();

export function planImport(a: SessionAnalysisV1, ctx: PlanContext): ImportPlan {
  const warnings: string[] = [];
  const issues: ImportPlan['issues'] = [];
  const needsApproval: string[] = [];
  let segments = ctx.segments;
  let seg = new Map(segments.map((s) => [s.segmentId, s]));
  // 자료의 번호는 발언 단위(g12)일 수도, 문장 단위(g12-1)일 수도 있다
  const resolve = (id: string): TranscriptSegment[] => {
    const exact = seg.get(id);
    return exact ? [exact] : segments.filter((s) => s.segmentId.startsWith(`${id}-`));
  };

  // ---- 화자 검토 ----
  const roleReviews: ImportPlan['roleReviews'] = [];
  for (const r of a.roleReviewProposals) {
    for (const s of resolve(r.segmentId)) {
      // 상담자가 정한 줄은 그대로. 자동 판정은 확실(내/상)로 표시됐어도 AI가 반대라고 보면 바꾸되 '추정'으로 남긴다
      if (s.roleSource === 'user' || s.clinicalRole === r.proposedRole) continue;
      if (roleReviews.some((x) => x.segmentId === s.segmentId)) continue;
      roleReviews.push({
        segmentId: s.segmentId,
        role: r.proposedRole,
        from: s.clinicalRole,
        text: s.text,
        ...(r.reason ? { reason: r.reason } : {}),
      });
    }
  }

  // 아래 판단(내담자 진술 게이트 등)은 AI가 고친 화자 기준으로 한다 — 반영 후 저장될 상태와 같게
  if (roleReviews.length) {
    const fix = new Map(roleReviews.map((r) => [r.segmentId, r.role]));
    segments = segments.map((s) =>
      fix.has(s.segmentId)
        ? {
            ...s,
            clinicalRole: fix.get(s.segmentId)!,
            roleSource: 'ai-review' as const,
            roleConfidence: Math.min(AI_ROLE_CONFIDENCE_CAP, Math.max(s.roleConfidence, 0.75)),
          }
        : s,
    );
    seg = new Map(segments.map((s) => [s.segmentId, s]));
  }
  const resolveAll = (ids: string[]) => ids.flatMap(resolve);
  const item = (text: string, status: 'observation' | 'hypothesis'): AnalysisItem => ({
    id: ctx.newId(),
    text,
    status,
  });

  // ---- 근거 (CLIENT_REPORT gate) ----
  const evidence: PlannedEvidence[] = [];
  for (const r of a.clientReports) {
    const found = resolveAll(r.segmentIds);
    if (r.segmentIds.some((id) => !resolve(id).length)) {
      warnings.push(`내담자 진술 근거 중 없는 줄 번호가 있습니다: ${r.text}`);
      issues.push({
        kind: 'missing-line',
        message: '근거 줄 번호를 찾지 못함',
        text: r.text,
        segmentIds: found.map((s) => s.segmentId),
      });
    }
    const allClient = found.length > 0 && found.every((s) => s.clinicalRole === 'client');
    const minConf = found.length ? Math.min(...found.map((s) => (s.roleSource === 'user' ? 1 : s.roleConfidence))) : 0;
    const weak = found.some((s) => s.weakAgreement);
    const unverifiedText = found.some((s) => s.verificationStatus === 'needs-review');
    const strong = allClient && minConf >= CLIENT_REPORT_MIN_ROLE_CONFIDENCE && !weak && !unverifiedText;
    const role =
      found.length === 0
        ? 'unknown'
        : allClient
          ? 'client'
          : found.some((s) => s.clinicalRole === 'counselor')
            ? 'counselor'
            : 'unknown';
    if (role === 'counselor') {
      warnings.push(`AI가 상담자 발언을 내담자 진술로 인용했습니다 → 확인 필요로 저장: ${r.text}`);
      issues.push({
        kind: 'counselor-quoted',
        message: 'AI가 상담자 발언(또는 상담자로 판정된 줄)을 내담자 진술로 인용 → 확인 필요로 저장',
        text: r.text,
        segmentIds: found.map((s) => s.segmentId),
        lines: found.map((s) => ({
          segmentId: s.segmentId,
          role: s.clinicalRole,
          sure: s.roleSource === 'user' || s.roleConfidence >= 0.85,
          text: s.text,
        })),
      });
    }
    evidence.push({
      evidenceType: 'CLIENT_REPORT',
      text: untag(r.text),
      ...(found.length
        ? { sourceSegmentIds: found.map((s) => s.segmentId), sourceTimeMs: found[0]!.startMs ?? 0 }
        : {}),
      roleAtExtraction: role,
      roleConfidenceAtExtraction: Number(minConf.toFixed(3)),
      agreementStrength: weak ? 'weak-agreement' : 'explicit',
      needsVerification: !strong,
    });
  }
  for (const o of a.counselorObservations)
    evidence.push({ evidenceType: 'COUNSELOR_OBSERVATION', text: untag(o.text), needsVerification: false });
  for (const h of a.hypotheses)
    evidence.push({
      evidenceType: 'HYPOTHESIS',
      text: untag(h.text),
      ...(h.segmentIds.length ? { sourceSegmentIds: resolveAll(h.segmentIds).map((s) => s.segmentId) } : {}),
      needsVerification: false,
    });

  // ---- 위험 (제안) ----
  const riskEntries = Object.entries(a.risk).filter(([, v]) => v.status !== 'not_assessed' || v.followUpRequired);
  const risk = riskEntries.length
    ? Object.fromEntries(
        riskEntries.map(([k, v]) => [
          k,
          {
            status: v.status as 'denied' | 'present' | 'unclear',
            ...(v.followUpRequired ? { followUpRequired: true } : {}),
          },
        ]),
      )
    : null;
  if (risk) needsApproval.push(`위험도 변경 제안 ${riskEntries.length}항목`);

  // ---- 목표 (제안) ----
  const goals: ImportPlan['goals'] = [];
  for (const g of a.goalProgress) {
    const goal = ctx.goals.find((x) => x.id === g.goalId) ?? ctx.goals.find((x) => x.title === g.goalId);
    if (!goal) {
      warnings.push(`알 수 없는 목표 ID를 건너뜀: ${g.goalId}`);
      continue;
    }
    if (goal.status === g.proposedStatus && !g.note) continue;
    goals.push({
      goalId: goal.id,
      title: goal.title,
      from: goal.status,
      to: g.proposedStatus,
      ...(g.note ? { note: g.note } : {}),
    });
  }
  if (goals.some((g) => g.from !== g.to))
    needsApproval.push(`목표 상태 변경 제안 ${goals.filter((g) => g.from !== g.to).length}개`);

  // ---- 사례개념화 (새 revision 제안) ----
  let formulation: ImportPlan['formulation'] = null;
  if (a.formulation) {
    const unmatched: { kind: 'strengthened' | 'weakened' | 'rejected'; text: string }[] = [];
    const ids = (texts: string[], kind: 'strengthened' | 'weakened' | 'rejected') =>
      texts.flatMap((t) => {
        const id = matchHypothesis(t, ctx.formulation);
        if (!id && t.trim()) unmatched.push({ kind, text: t.trim() });
        return id ? [id] : [];
      });
    formulation = {
      ...(a.formulation.currentProblems.length ? { currentProblems: a.formulation.currentProblems } : {}),
      addedTexts: a.formulation.added.filter((t) => !matchHypothesis(t, ctx.formulation)),
      addedItems: Object.fromEntries(
        Object.entries(a.formulation.addedItems)
          .map(([k, texts]) => {
            const cur = (ctx.formulation as Record<string, unknown> | undefined)?.[k];
            const have = new Set(
              (Array.isArray(cur) ? cur : []).map((x: unknown) =>
                (typeof x === 'string' ? x : ((x as { text?: string }).text ?? '')).replace(/\s+/g, ''),
              ),
            );
            return [k, [...new Set(texts)].filter((t) => !have.has(t.replace(/\s+/g, '')))] as const;
          })
          .filter(([, v]) => v.length),
      ),
      strengthenedIds: ids(a.formulation.strengthened, 'strengthened'),
      weakenedIds: ids(a.formulation.weakened, 'weakened'),
      rejectedIds: ids(a.formulation.rejected, 'rejected'),
      ...(a.formulation.changeReason ? { changeReason: a.formulation.changeReason } : {}),
      unmatched,
      hypothesisOptions: FORMULATION_ITEM_FIELDS.flatMap((f) => ctx.formulation?.[f] ?? [])
        .filter((h) => h.text.trim() && !(ctx.formulation?.rejectedHypotheses ?? []).includes(h.id))
        .map((h) => ({ id: h.id, text: h.text })),
    };
    const n =
      formulation.addedTexts.length +
      Object.values(formulation.addedItems).reduce((s, v) => s + v.length, 0) +
      formulation.strengthenedIds.length +
      formulation.weakenedIds.length +
      formulation.rejectedIds.length +
      unmatched.length;
    if (n || formulation.currentProblems) needsApproval.push('사례개념화 개정안');
    else formulation = null;
  }

  // ---- 진단적 고려 (제안) ----
  let diagnosis = a.diagnosis;
  if (diagnosis) {
    diagnosis = {
      ...diagnosis,
      considerations: diagnosis.considerations.map((c) => {
        if (c.status === 'externally_confirmed') {
          warnings.push(`AI는 공식 진단을 확정할 수 없어 "${c.diagnosisName}"을(를) '가능성 있음'으로 낮췄습니다`);
          return { ...c, status: 'possible' };
        }
        return c;
      }),
    };
    if (diagnosis.considerations.length || diagnosis.symptomList.length) needsApproval.push('진단적 고려 개정안');
    else diagnosis = null;
  }

  // ---- 상담기록지 초안 ----
  let journal: ImportPlan['journal'] = null;
  const jd = a.journalDraft;
  if (Object.keys(jd).length) {
    if (ctx.journal && ctx.journal.status === 'approved') {
      warnings.push('상담기록지가 이미 승인되어 AI 초안을 반영하지 않았습니다 (원문은 보관됨)');
    } else {
      const fields: Record<string, string> = {};
      const skipped: string[] = [];
      for (const [k, v] of Object.entries(jd)) {
        const src = ctx.journal?.fieldSources?.[k];
        const cur = (ctx.journal as Record<string, unknown> | null | undefined)?.[k];
        if (src === 'user' && typeof cur === 'string' && cur.trim()) skipped.push(k);
        else fields[k] = v;
      }
      journal = { fields, skipped };
      if (skipped.length) warnings.push(`상담자가 직접 쓴 칸 ${skipped.length}개는 덮어쓰지 않았습니다`);
    }
  }

  // ---- 축어록 추천 ----
  const order = new Map(segments.map((s, i) => [s.segmentId, i]));
  const verbatims: ImportPlan['verbatims'] = [];
  for (const v of a.verbatimSuggestions) {
    const startSegs = resolve(v.startSegmentId);
    const endSegs = resolve(v.endSegmentId);
    const i0 = startSegs.length ? order.get(startSegs[0]!.segmentId) : undefined;
    const i1 = endSegs.length ? order.get(endSegs.at(-1)!.segmentId) : undefined;
    if (i0 === undefined || i1 === undefined || i1 < i0) {
      warnings.push('축어록 추천 중 줄 번호를 찾지 못한 항목을 건너뜀');
      continue;
    }
    const segs = segments.slice(i0, i1 + 1).filter((s) => !s.isBackchannel);
    verbatims.push({
      segmentIds: segs.map((s) => s.segmentId),
      startMs: segs[0]?.startMs ?? 0,
      endMs: segs.at(-1)?.startMs ?? 0,
      ...(v.reason ? { reason: v.reason } : {}),
      supervisionQuestions: v.supervisionQuestions,
    });
  }

  // ---- 내담자 정보 덧붙이기: 인용 줄이 모두 확실한 내담자 발언일 때만 ----
  const profile = a.profileUpdates.map((p) => {
    const found = resolveAll(p.segmentIds);
    const sure =
      found.length > 0 &&
      found.every(
        (s) =>
          s.clinicalRole === 'client' &&
          (s.roleSource === 'user' || s.roleConfidence >= CLIENT_REPORT_MIN_ROLE_CONFIDENCE) &&
          s.verificationStatus !== 'needs-review' &&
          !s.weakAgreement,
      );
    const reason = !found.length
      ? '근거 발언이 없음'
      : found.some((s) => s.clinicalRole === 'counselor')
        ? '상담자 발언을 근거로 함'
        : sure
          ? undefined
          : '화자·전사가 불확실한 줄';
    return { ...p, ok: sure, ...(reason ? { reason } : {}) };
  });

  return {
    analysis: {
      ...(a.sessionSummary ? { sessionSummary: a.sessionSummary } : {}),
      keyEvents: a.keyEvents,
      notableChanges: a.notableChanges,
      emotions: a.emotions.map((t) => item(t, 'observation')),
      cognitions: a.cognitions.map((t) => item(t, 'hypothesis')),
      behaviors: a.behaviors.map((t) => item(t, 'observation')),
      relationshipPatterns: a.relationshipPatterns.map((t) => item(t, 'hypothesis')),
      interventions: a.interventionsUsed.map((x) => ({
        interventionName: x.name,
        ...(x.orientation ? { theoreticalOrientation: x.orientation } : {}),
        ...(x.clientResponse ? { clientResponse: x.clientResponse } : {}),
        ...(x.followUpNeeded ? { followUpNeeded: true } : {}),
      })),
    },
    evidence,
    risk,
    goals,
    formulation,
    diagnosis,
    journal,
    roleReviews,
    approachEvidence: a.approachEvidence.map((x) => ({
      ...x,
      segmentIds: [...new Set(resolveAll(x.segmentIds).map((s) => s.segmentId))],
    })),
    verbatims,
    questions: a.questionsForCounselorReview,
    goalSuggestions: a.goalSuggestions,
    issues,
    dialogueDraft: a.dialogueDraft,
    selfReview: a.selfReview,
    directionPlan: a.directionPlan,
    changePoints: a.changePoints,
    missingSections: a.missingSections,
    profile,
    warnings,
    needsApproval,
  };
}
